import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { IMG, PAGE_CONTENT } from "@/lib/constants";

const IMAGES = [
  { src: IMG.g1 },
  { src: IMG.g2 },
  { src: IMG.g3 },
  { src: IMG.g4 },
  { src: IMG.g5 },
  { src: IMG.g6 },
  { src: IMG.g7 },
  { src: IMG.g8 },
  { src: IMG.g9 },
  { src: IMG.g10 },
  { src: IMG.g11 },
  { src: IMG.g12 },
  { src: IMG.g13 },
  { src: IMG.heroAlt },
];

export default function Gallery() {
  const [idx, setIdx] = useState(null);
  const [cols, setCols] = useState(3);

  const close = () => setIdx(null);
  const next = () => setIdx((i) => (i === null ? 0 : (i + 1) % IMAGES.length));
  const prev = () => setIdx((i) => (i === null ? 0 : (i - 1 + IMAGES.length) % IMAGES.length));

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCols(1);
      } else if (window.innerWidth < 1024) {
        setCols(2);
      } else {
        setCols(3);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (idx === null) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);

  }, [idx]);

  // Distribute images into columns dynamically
  const columnData = Array.from({ length: cols }, () => []);
  IMAGES.forEach((im, i) => {
    columnData[i % cols].push({ ...im, globalIdx: i });
  });

  return (
    <div data-testid="gallery-page">
      <section className="pt-40 pb-16 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="font-display text-[11px] uppercase tracking-[0.4em] text-copper mb-5">{PAGE_CONTENT.gallery.hero.kicker}</div>
          <h1 className="font-serif text-6xl md:text-8xl leading-[0.9] tracking-tighter">
            {PAGE_CONTENT.gallery.hero.title1} <em className="italic font-light text-copper">{PAGE_CONTENT.gallery.hero.title2}</em>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-parchment/70 leading-relaxed">
            {PAGE_CONTENT.gallery.hero.desc}
          </p>
        </motion.div>
      </section>

      {/* masonry */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-32">
        <div className={`grid gap-8 ${cols === 1 ? "grid-cols-1" : cols === 2 ? "grid-cols-2" : "grid-cols-3"} items-start`}>
          {columnData.map((colItems, colIdx) => {
            // compute dynamic offset classes
            let offsetClass = "";
            if (cols === 3) {
              if (colIdx === 1) offsetClass = "mt-20";
              if (colIdx === 2) offsetClass = "mt-10";
            } else if (cols === 2) {
              if (colIdx === 1) offsetClass = "mt-12";
            }

            return (
              <div key={colIdx} className={`flex flex-col gap-8 ${offsetClass}`}>
                {colItems.map((im) => (
                  <motion.button
                    key={im.globalIdx}
                    onClick={() => setIdx(im.globalIdx)}
                    data-testid={`gallery-item-${im.globalIdx}`}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.8, delay: (im.globalIdx % 6) * 0.05 }}
                    className="block w-full overflow-hidden group relative border border-line hover:border-copper/60 hover:shadow-[0_0_25px_rgba(200,155,95,0.12)] hover:-translate-y-1.5 transition-all duration-500"
                  >
                    <img src={im.src} alt="Gallery item" className="w-full h-auto object-cover kenburns-img" />
                    <div className="absolute inset-0 bg-ink/10 group-hover:bg-transparent transition-colors duration-500" />
                  </motion.button>
                ))}
              </div>
            );
          })}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {idx !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-md flex items-center justify-center"
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button onClick={close} className="absolute top-6 right-6 text-parchment" aria-label="close" data-testid="lightbox-close">
              <X size={28} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 md:left-10 text-parchment" aria-label="previous" data-testid="lightbox-prev">
              <ChevronLeft size={40} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 md:right-10 text-parchment" aria-label="next" data-testid="lightbox-next">
              <ChevronRight size={40} />
            </button>
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="max-w-6xl w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={IMAGES[idx].src} alt="Gallery item" className="w-full max-h-[85vh] object-contain" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
